import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useBasket } from '@/lib/cart';
import { useTheme } from '@/hooks/useTheme';
import { LOGIN_ENABLED } from '@/lib/features';

export default function Navbar() {
  const { user, isAdmin, signOut, signIn } = useAuth();
  const { items, removeItem, getItemCount, getTotalPrice } = useBasket();
  const { isLight, toggle: toggleTheme } = useTheme();
  const loc   = useLocation();
  const nav   = useNavigate();
  const [cartOpen, setCartOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const showAdmin = LOGIN_ENABLED && isAdmin;
  const count = getItemCount();
  const total = getTotalPrice();

  return (
    <>
      {/* ── Nav bar ── */}
      <nav className="sticky top-0 z-50 bg-black border-b border-[#27272A]
                      shadow-[0_1px_0_#27272A]">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-4">

          {/* Logo + hamburger */}
          <div className="flex items-center gap-2 shrink-0">
            <Link to="/" className="flex items-center gap-3 shrink-0">
              <img
                src="/logo.png"
                alt="MrPanadol"
                className="logo-bg w-9 h-9 rounded-full object-contain
                           shadow-[0_0_16px_rgba(251,191,36,0.3)]"
              />
              <span className="font-bold text-lg text-white tracking-tight hidden sm:block">
                MrPanadol
              </span>
            </Link>

            {/* Hamburger — opens a menu with Home / Courses / Online Sessions */}
            <div className="relative">
              <button
                onClick={() => setMenuOpen(o => !o)}
                aria-label="Open menu"
                className="w-10 h-10 flex items-center justify-center
                           text-[#A1A1AA] hover:text-white transition-colors rounded-xl
                           hover:bg-[#18181B]"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24"
                     stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round"
                    d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>

              {menuOpen && (
                <div className="absolute left-0 top-12 w-52 bg-[#18181B]
                                rounded-3xl border border-[#27272A]
                                shadow-[0_8px_32px_rgba(0,0,0,0.6)] z-50 overflow-hidden">
                  {[
                    { to: '/',              label: 'Home' },
                    { to: '/courses',       label: 'Courses' },
                    ...(LOGIN_ENABLED ? [{ to: '/booking', label: 'Online Sessions' }] : []),
                    ...(LOGIN_ENABLED && user && !isAdmin ? [{ to: '/my-courses', label: 'My Library' }] : []),
                    ...(showAdmin ? [{ to: '/admin', label: 'Admin' }] : []),
                  ].map(item => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setMenuOpen(false)}
                      className={`block px-4 py-3 text-sm font-semibold transition-colors
                                  ${loc.pathname === item.to
                                    ? 'text-yellow-400 bg-yellow-400/10'
                                    : 'text-[#E4E4E7] hover:bg-[#27272A] hover:text-white'}`}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Centre — admin shortcut only; everything else lives in the menu */}
          <div className="flex-1 flex items-center justify-end md:justify-center">
            {showAdmin && (
              <Link
                to="/admin"
                className={`text-sm font-semibold transition-colors ${
                  loc.pathname.startsWith('/admin')
                    ? 'text-red-400'
                    : 'text-[#71717A] hover:text-red-400'
                }`}
              >
                Admin
              </Link>
            )}
          </div>

          {/* Right */}
          <div className="flex items-center gap-2">

            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              aria-label={isLight ? 'Switch to night mode' : 'Switch to day mode'}
              title={isLight ? 'Switch to night mode' : 'Switch to day mode'}
              className="w-10 h-10 flex items-center justify-center
                         text-[#A1A1AA] hover:text-yellow-400 transition-colors rounded-xl
                         hover:bg-[#18181B]"
            >
              {isLight ? (
                /* Moon (we're in light mode — clicking goes back to dark) */
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24"
                     stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round"
                    d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z" />
                </svg>
              ) : (
                /* Sun (we're in dark mode — clicking switches to light) */
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24"
                     stroke="currentColor" strokeWidth={2}>
                  <circle cx="12" cy="12" r="4" />
                  <path strokeLinecap="round" d="M12 2v2m0 16v2M2 12h2m16 0h2
                                                 M4.93 4.93l1.41 1.41
                                                 M17.66 17.66l1.41 1.41
                                                 M4.93 19.07l1.41-1.41
                                                 M17.66 6.34l1.41-1.41" />
                </svg>
              )}
            </button>

            {/* Cart — checkout needs an account, so hidden while login is off */}
            {LOGIN_ENABLED && <div className="relative">
              <button
                onClick={() => setCartOpen(o => !o)}
                aria-label={`Basket — ${count} item${count !== 1 ? 's' : ''}`}
                className="relative w-10 h-10 flex items-center justify-center
                           text-[#A1A1AA] hover:text-white transition-colors rounded-xl
                           hover:bg-[#18181B]"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24"
                     stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round"
                    d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25
                       a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684
                       2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5
                       14.25L5.106 5.272M6 20.25a.75.75 0 110-1.5.75.75 0 010
                       1.5zm12.75 0a.75.75 0 110-1.5.75.75 0 010 1.5z" />
                </svg>
                {count > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] px-1
                                   bg-yellow-400 text-black text-[10px] font-bold
                                   rounded-full flex items-center justify-center">
                    {count}
                  </span>
                )}
              </button>

              {/* Cart dropdown */}
              {cartOpen && (
                <div className="absolute right-0 top-12 w-80 bg-[#18181B]
                                rounded-3xl border border-[#27272A]
                                shadow-[0_8px_32px_rgba(0,0,0,0.6)] z-50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-[#27272A] flex items-center justify-between">
                    <span className="text-white font-semibold text-sm">
                      Basket{count > 0 ? ` (${count})` : ''}
                    </span>
                    <button
                      onClick={() => setCartOpen(false)}
                      className="text-[#71717A] hover:text-white text-lg leading-none transition-colors"
                    >
                      ✕
                    </button>
                  </div>

                  {items.length === 0 ? (
                    <div className="px-4 py-10 text-center">
                      <p className="text-[#52525B] text-sm">Your basket is empty</p>
                    </div>
                  ) : (
                    <>
                      <ul className="max-h-52 overflow-y-auto divide-y divide-[#27272A]">
                        {items.map(item => (
                          <li key={item.id} className="flex items-center justify-between px-4 py-3">
                            <div className="min-w-0 mr-3">
                              <p className="text-[#E4E4E7] text-xs font-semibold truncate">
                                {item.title}
                              </p>
                              <p className="text-yellow-400 text-xs font-bold mt-0.5">
                                {item.price === 0 ? 'Free' : `${item.price} KD`}
                              </p>
                            </div>
                            <button
                              onClick={() => removeItem(item.id)}
                              className="shrink-0 text-[#52525B] hover:text-red-400
                                         text-xs transition-colors"
                            >
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                      <div className="px-4 py-3 border-t border-[#27272A]">
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-[#A1A1AA] text-sm">Total</span>
                          <span className="text-yellow-400 font-bold text-base">{total} KD</span>
                        </div>
                        <button
                          onClick={() => { setCartOpen(false); nav('/basket'); }}
                          className="mp-btn mp-btn-primary w-full py-2.5 rounded-2xl"
                        >
                          View Basket & Checkout
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>}

            {/* Auth */}
            {!LOGIN_ENABLED ? null : user ? (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-yellow-400 flex items-center justify-center
                                text-black font-bold text-xs shrink-0">
                  {user.name[0].toUpperCase()}
                </div>
                <span className="hidden lg:block text-sm text-[#A1A1AA] max-w-[96px] truncate">
                  {user.name}
                </span>
                <button
                  onClick={signOut}
                  className="mp-btn mp-btn-ghost text-sm px-4 py-1.5"
                >
                  Logout
                </button>
              </div>
            ) : (
              <button
                onClick={() => signIn(loc.pathname)}
                className="mp-btn mp-btn-primary px-5 py-2"
              >
                Login
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Click-outside dismissal for both the cart dropdown and the menu */}
      {(cartOpen || menuOpen) && (
        <div className="fixed inset-0 z-40"
             onClick={() => { setCartOpen(false); setMenuOpen(false); }} />
      )}
    </>
  );
}
